import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import AddButton from "components/AddButton";
import ConfirmBoxDeleteTS from "components/ConfirmBoxDeleteTS";
import EditButton from "components/EditButton";
import MaterialTableTS from "components/tables/MaterialTableTS";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import TableFilters from "../TableFilters";
import { PolicyTermType, recordIdType } from "../types";
import {
  addStatePolicyTerm,
  editStatePolicyTerm,
  rowEditIdStatePolicyTerm,
  selectedRowsStatePolicyTerm,
  setFilterTextPolicyTerm,
} from "./atoms/atoms";
import { PolicyTermForm } from "./forms/PolicyTermForm";
import useDeletePolicyTerm from "./hooks/useDeletePolicyTerm";
import usePolicyTerm from "./hooks/usePolicyTerm";

const PolicyTermTable = () => {
  //Hooks Call
  const { data, isFetching } = usePolicyTerm();
  const BulkDeletePolicyTermType = useDeletePolicyTerm();
  //Atoms Call
  const [actionAddState, setActionAddState] =
    useRecoilState(addStatePolicyTerm);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStatePolicyTerm
  );
  const [actionEditState, setActionEditState] =
    useRecoilState(editStatePolicyTerm);
  const [toEdit, setToEdit] = useRecoilState(rowEditIdStatePolicyTerm);

  const [rowsCount, setRowsCount] = useState(0);
  const [filterText, setFilterText] = useRecoilState(setFilterTextPolicyTerm);
  //Local States
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [isBulkSelected, setIsBulkSelected] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const [policyTermFilteredData, setPolicyTermFilteredData] = useState<
    PolicyTermType[]
  >([]);

  const addPermission = PERMISSIONS.permission_utilities_policyterm_terms_add;
  const editPermission = PERMISSIONS.permission_utilities_policyterm_terms_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_policyterm_terms_delete;

  const userPermissions = useRecoilValue(permissionsSelector);

  const [empNewData] = useState<PolicyTermType[]>([]);
  useEffect(() => {}, [data]);

  //For filtered data update
  const [polTermId, setPolTermId] = useState("");
  useEffect(() => {
    if (polTermId?.includes(".")) {
      let ids = polTermId.split(".");
      const arr = ids.map((id) => Number(id));
      const filteredData = data?.filter((x) => arr.includes(x.policyTermId));
      setPolicyTermFilteredData(filteredData);
    } else {
      const filteredData = data?.filter(
        (x) => x.policyTermId === Number(polTermId)
      );
      setPolicyTermFilteredData(filteredData);
    }
  }, [polTermId, data]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent: recordIdType;
  if (location.state != null) {
    recordIdFromRecent!.locationState = location.state as string;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setPolicyTermFilteredData(result);
          setPolTermId(recordIdFromRecent.recordId);
          history.replace("");
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, []);

  useEffect(() => {
    setActionAddState(false);
    setActionEditState(false);
    setSelectedRows([]);
  }, []);

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
    setIsBulkSelected((prev) => !prev);
  };

  const handleEditButton = (rowData) => {
    setToEdit({ ...rowData });
    empNewData.map((x) => (x.isActive = rowData.isActive));
    empNewData.map((x) => (x.policyTermId = rowData.policyTermId));
    empNewData.map((x) => (x.polTerm = rowData.polTerm));
    empNewData.map((x) => (x.policyTermName = rowData.policyTermName));
    empNewData.map((x) => (x.createdBy = rowData.createdBy));
    empNewData.map((x) => (x.createdDate = rowData.createdDate));
  };
  // end of saving changes

  const handleBulkDelete = () => {
    const ids = selectedRows.map((row) => row.policyTermId);
    BulkDeletePolicyTermType(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    setIsBulkSelected(false);
    setDisabledButton(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Policy Terms" : "Policy Term"
    }?`}</div>
  );

  useEffect(() => {
    if (!toEdit) {
      setDisabledButton(false);
    }
  }, [toEdit]);

  const matColumns = [
    {
      title: "Term",
      field: "polTerm",
      render: (rowData) => {
        return (
          <span className="text-quinary font-bold uppercase text-sm">
            {rowData.polTerm}
          </span>
        );
      },
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },

    {
      title: "Status",
      field: "isActive",
      align: "left",
      render: (rowData) => {
        return (
          <div>
            <div className="font-sans text-left mr-4 text-xs">
              {rowData.isActive ? "Active" : "Inactive"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action/s",
      align: "center",
      sorting: false,
      render: (rowData) => {
        return !userPermissions.includes(editPermission) ? (
          ""
        ) : (
          <div className=" flex items-center place-content-center ">
            <EditButton
              disabled={disabledButton || selectedRows.length > 0}
              className={
                disabledButton || selectedRows.length > 0
                  ? "opacity-50 rounded-full items-center bg-quaternarytext-white hover:bg-hquaternary"
                  : "opacity-100 rounded-full items-center bg-quaternarytext-white hover:bg-hquaternary"
              }
              onClick={() => {
                setDisabledButton(true);
                setActionEditState(true);
                handleEditButton(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  // Filters
  const filteredItems = data
    ? data.filter((item) => item.polTerm.toString() === filterText?.trim())
    : [];

  const editOrAdd = actionAddState || actionEditState;

  return (
    <div className="bg-white animate-fade-in-down p-4">
      {!editOrAdd && (
        <>
          <div className="flex flex-row">
            <div className="w-1/2">
              <TableFilters
                defaultValue={filterText}
                filterText={setFilterText}
                moduleDetails="PolicyTerm"
                textInputName="PolicyTermName"
                columnDetails="Policy Term"
                disabledButton={disabledButton || selectedRows.length > 0}
              ></TableFilters>
            </div>
            <div className="w-1/2 ">
              {!userPermissions.includes(addPermission) ? (
                ""
              ) : (
                <AddButton
                  buttonText="New Policy Term"
                  className={
                    selectedRows.length > 0 || actionEditState
                      ? "opacity-50 float-right"
                      : "opacity-100 float-right"
                  }
                  onClick={() => setActionAddState(!actionAddState)}
                  disabled={disabledButton || selectedRows.length > 0}
                />
              )}
            </div>
          </div>
          <div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>
          </div>
          <div className="">
            <MaterialTableTS
              columns={matColumns as any}
              data={
                policyTermFilteredData?.length > 0
                  ? policyTermFilteredData
                  : data
                  ? filterText === ""
                    ? data
                    : filteredItems
                  : []
              }
              selection={
                !actionEditState && userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              isLoading={isFetching}
              showTitle={true}
            />
          </div>

          <ConfirmBoxDeleteTS
            ModalTitle="Deactivate Policy Term"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => setOpenModalDelete(false)}
            onClickFunction={
              isBulkSelected ? handleBulkDelete : handleBulkDelete
            }
          />
        </>
      )}
      {editOrAdd && (
        <div className="">
          <PolicyTermForm />
        </div>
      )}
    </div>
  );
};

export default PolicyTermTable;
